import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, matchPath } from "react-router";
import { BREADCRUMB_ROUTES } from "@/app/router/breadcrumbs.config";
import type { Crumb } from "@/shared/ui/Breadcrumbs";
import { BreadcrumbContext } from "./breadcrumbContext";

const BREADCRUMB_NAMESPACES = [
  ...new Set(
    BREADCRUMB_ROUTES.flatMap((r) => r.breadcrumbs)
      .map((b) => b.labelKey?.split(":")[0])
      .filter(Boolean) as string[],
  ),
];

export function useBreadcrumbs(): Crumb[] {
  const { pathname, search } = useLocation();
  const { t, ready } = useTranslation(BREADCRUMB_NAMESPACES);
  const { overrides } = useContext(BreadcrumbContext);

  return useMemo(function resolveBreadcrumbs() {
    if (!ready) return [];

    for (const route of BREADCRUMB_ROUTES) {
      const match = matchPath(route.path, pathname);
      if (!match) continue;

      const params = (match.params as Record<string, string>) ?? {};
      const crumbs: Crumb[] = [];

      for (const config of route.breadcrumbs) {
        let label: string | null = "";

        if (config.labelKey) {
          label = t(config.labelKey);
        } else if (config.getDynamicLabel) {
          label = config.getDynamicLabel(params, overrides, t);
        }

        if (label === null) {
          crumbs.push({ label: "", loading: true });
          continue;
        }

        if (!label) continue;

        const crumb: Crumb = { label };

        if (config.href) {
          crumb.href = typeof config.href === "function" ? config.href(params, new URLSearchParams(search)) : config.href;
        }

        crumbs.push(crumb);
      }

      return crumbs;
    }

    return [];
  }, [pathname, search, t, overrides, ready]);
}
